import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    // ⚠ La galería del modal se desliza en HORIZONTAL, así que hay que devolverla a la primera
    // foto a mano: si no, al reabrir el modal se queda donde la dejó la vez anterior.
    dialogComponent.querySelector('.media-gallery__grid')?.scrollTo({ left: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Construye la fila de miniaturas bajo la galería del modal y la mantiene sincronizada.
   * @param {Element} modalContent - El contenido inyectado en el modal
   */
  #montarMiniaturas(modalContent) {
    const columna = modalContent.querySelector('.product-information__media');
    const galeria = columna?.querySelector('.media-gallery__grid');
    if (!columna || !galeria) return;

    columna.querySelector('.lat-miniaturas')?.remove();
    const fotos = [...galeria.children].filter((e) => e.querySelector('img, video'));
    if (fotos.length < 2) return;

    const tira = document.createElement('div');
    tira.className = 'lat-miniaturas';

    // Se declara antes del bucle porque los botones la llaman al terminar de desplazarse.
    let marcarActiva = () => {};

    const botones = fotos.map((foto, i) => {
      const img = foto.querySelector('img');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lat-miniaturas__boton';
      b.setAttribute('aria-label', `Ver la foto ${i + 1} de ${fotos.length}`);
      if (img) {
        const mini = document.createElement('img');
        mini.src = img.currentSrc || img.src;
        mini.alt = '';
        b.appendChild(mini);
      }
      b.addEventListener('click', () => {
        const destino = foto.offsetLeft - galeria.offsetLeft;

        // ⚠ POR QUÉ ESTO NO ES UN `scrollTo` A SECAS (Yeremi, 2026-08-18: "le doy a una imagen y
        // no cambia, solo aparece la principal"). La galería lleva `scroll-snap-type: x mandatory`
        // y **Safari de iPhone cancela el desplazamiento suave programático** en un contenedor con
        // snap: la vuelve a anclar en la foto donde estaba. En Chrome —y por eso no salía en las
        // pruebas— funciona igual con snap. Se apaga el snap mientras dura el movimiento y, si aun
        // así no llegó, se salta a la posición sin animación.
        const snapPrevio = galeria.style.scrollSnapType;
        galeria.style.scrollSnapType = 'none';
        galeria.scrollTo({ left: destino, behavior: 'smooth' });

        setTimeout(() => {
          if (Math.abs(galeria.scrollLeft - destino) > 4) galeria.scrollLeft = destino;
          galeria.style.scrollSnapType = snapPrevio;
          marcarActiva();
        }, 400);
      });
      tira.appendChild(b);
      return b;
    });

    // ⚠ ESTO ES LO QUE SEÑALABA SIEMPRE LA ÚLTIMA FOTO (Yeremi, 2026-08-18: "el selector de
    // fotos aparece en la última foto aunque estés viendo la primera"). La versión anterior
    // marcaba "activa" toda foto cuyo borde izquierdo quedara antes del centro de la ventana;
    // como se llamaba ANTES de que el navegador midiera la fila, las cinco fotos daban x = 0 y
    // el bucle terminaba dejando marcada la última. Ahora se elige la MÁS CERCANA al centro
    // —que no depende del orden— y se mide después de pintar.
    marcarActiva = () => {
      const centro = galeria.scrollLeft + galeria.clientWidth / 2;
      let activa = 0;
      let mejor = Infinity;
      fotos.forEach((foto, i) => {
        const medio = foto.offsetLeft - galeria.offsetLeft + foto.offsetWidth / 2;
        const dist = Math.abs(medio - centro);
        if (dist < mejor) {
          mejor = dist;
          activa = i;
        }
      });
      botones.forEach((b, i) => b.classList.toggle('is-activa', i === activa));
    };
    galeria.addEventListener('scroll', marcarActiva, { passive: true });

    columna.appendChild(tira);
    // Dos cuadros: al primero la fila recién insertada todavía no tiene medidas.
    requestAnimationFrame(() => requestAnimationFrame(marcarActiva));
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      // La valoración va entre el título y el precio, igual que en la ficha (Yeremi,
      // 2026-08-18). En celular el modal reconstruye la cabecera a mano, así que si no se
      // rescata aquí se pierde con el resto de `.product-details`.
      const valoracion = productGrid.querySelector('.lat-estrellas');
      if (valoracion) {
        productHeader.appendChild(valoracion);
      }
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      // ⚠ Antes de tirar `.product-details` hay que RESCATAR lo que solo vive ahí dentro, o en
      // celular el modal se queda sin el resumen de envío y pago, sin las reseñas, sin el enlace
      // a la ficha completa y sin el panel de la guía de tallas (que entonces no abre nada).
      const extras = productDetails?.querySelector('.lat-modal-extra');
      if (extras) productGrid.appendChild(extras);
      // TODOS los paneles, no solo el primero: la guía de tallas es el último de la lista y
      // con `querySelector` se quedaba fuera (por eso en celular el enlace no abría nada).
      productDetails?.querySelectorAll('.pdp-panel').forEach((panel) => productGrid.appendChild(panel));

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // ⚠ FOTOS BORROSAS (2026-08-18). Las imágenes llegan con el `sizes` de la FICHA, que se
    // calcula sobre el ancho de aquella página, no sobre el hueco que ocupan aquí. El navegador
    // elegía del `srcset` una versión de 387 px para una caja de 384 px, que en una pantalla
    // retina (x2) se ve borrosa. Se les fija el `sizes` al ancho real que ocupan en el modal:
    // el navegador vuelve a elegir y ya multiplica por la densidad de la pantalla.
    // Dos `requestAnimationFrame` seguidos: al primero las imágenes todavía miden 0 y el ajuste
    // se saltaba. Si aun así no se pueden medir, se usa el ancho de la columna de fotos y, en
    // último caso, un valor fijo generoso — nunca se deja el `sizes` de la ficha.
    const afinarFotos = () => {
      const columna = modalContent.querySelector('.product-information__media');
      const anchoColumna = Math.ceil(columna?.getBoundingClientRect().width || 0);
      // El ancho va en píxeles CSS, SIN multiplicar por la densidad: de eso ya se encarga el
      // navegador al elegir del `srcset`. (Se probó multiplicando y pedía el doble de lo
      // necesario: 1600 px para una caja de 384.)
      modalContent.querySelectorAll('img[srcset]').forEach((img) => {
        const propio = Math.ceil(img.getBoundingClientRect().width);
        const ancho = propio > 1 ? propio : anchoColumna > 1 ? anchoColumna : 420;
        img.sizes = `${ancho}px`;
      });
    };
    requestAnimationFrame(() => requestAnimationFrame(afinarFotos));

    // Las estrellas de la ficha enlazan a `#judgeme_product_reviews`, un ancla que en la
    // colección no existe: dentro del modal el enlace no llevaba a ninguna parte. Se reescribe
    // para que abra la ficha completa justo en las reseñas.
    const enlaceEstrellas = modalContent.querySelector('.lat-estrellas--enlace');
    if (enlaceEstrellas instanceof HTMLAnchorElement) {
      const url = this.productPageUrl;
      if (url) enlaceEstrellas.href = `${url.split('#')[0]}#judgeme_product_reviews`;
    }

    // ---- Miniaturas para cambiar de foto ------------------------------------------------
    // El carrusel se pasa deslizando, pero sin nada en qué tocar mucha gente no descubre que hay
    // más fotos (Yeremi, 2026-08-18: "no tiene opción para cambiar de imagen"). Se añade una fila
    // de miniaturas debajo, que además dice cuántas fotos hay de un vistazo.
    requestAnimationFrame(() => this.#montarMiniaturas(modalContent));

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
